// Productcatalogus. Elk product speelt anders: warme producten koelen af, koude producten smelten,
// gebak is kwetsbaar en de bruidstaart vult de hele bak.

import { t, type MessageKey } from '../i18n';

/** Hoe de temperatuur van een product zich gedraagt onderweg. */
export type HeatMode = 'warm' | 'koud' | 'geen';

export interface ScoreWeights {
  /** Stiptheid */
  time: number;
  /** Temperatuur (alleen bij warm of koud) */
  temp: number;
  /** Gaafheid: geen schokken en botsingen */
  care: number;
}

export interface Product {
  id: string;
  name: string;
  icon: string;
  items: string[];
  heat: HeatMode;
  /** Procentpunt verlies per seconde zonder bak (afkoelen of smelten) */
  tempLossPerSec: number;
  /** Vermenigvuldiger voor schade bij schokken */
  sensitivity: number;
  /** Basisvergoeding in euro's */
  basePay: number;
  /** Maximale fooi in euro's bij een perfecte levering */
  maxTip: number;
  weights: ScoreWeights;
  /** Vanaf welke rang dit product wordt aangeboden */
  minRank: number;
  /** Relatieve kans om aangeboden te worden */
  weight: number;
  /** Neemt de hele bak in beslag en mag alleen worden vervoerd */
  fillsBox?: boolean;
  /** Extra tijd bovenop de normale deadline (factor) */
  deadlineFactor?: number;
  /** Korte uitleg voor de speler bij het aanbod */
  tip: string;
}

export const PRODUCTS: readonly Product[] = [
  {
    id: 'koffie',
    get name() { return t('product.koffie.name' as MessageKey); },
    icon: '☕',
    items: ['2x grote koffie', '2x warme appelbol'],
    heat: 'warm',
    tempLossPerSec: 0.9,
    sensitivity: 0.7,
    basePay: 6,
    maxTip: 4,
    weights: { time: 0.5, temp: 0.4, care: 0.1 },
    minRank: 1,
    weight: 4,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'ontbijt',
    get name() { return t('product.ontbijt.name' as MessageKey); },
    icon: '🥐',
    items: ['3x croissant', '2x latte', '1x kaneelbroodje'],
    heat: 'warm',
    tempLossPerSec: 0.6,
    sensitivity: 0.9,
    basePay: 7.5,
    maxTip: 4.5,
    weights: { time: 0.45, temp: 0.35, care: 0.2 },
    minRank: 1,
    weight: 4,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'lunch',
    get name() { return t('product.lunch.name' as MessageKey); },
    icon: '🥪',
    items: ['4x saucijzenbroodje', '2x panini', '4x jus'],
    heat: 'warm',
    tempLossPerSec: 0.5,
    sensitivity: 1,
    basePay: 9,
    maxTip: 5,
    weights: { time: 0.45, temp: 0.3, care: 0.25 },
    minRank: 1,
    weight: 3,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'brood',
    get name() { return t('product.brood.name' as MessageKey); },
    icon: '🍞',
    items: ['4x roggebrood', '6x krentenbol'],
    heat: 'geen',
    tempLossPerSec: 0,
    sensitivity: 0.4,
    basePay: 6.5,
    maxTip: 3,
    weights: { time: 0.7, temp: 0, care: 0.3 },
    minRank: 1,
    weight: 3,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'slagroomtaart',
    get name() { return t('product.slagroomtaart.name' as MessageKey); },
    icon: '🎂',
    items: ['1x slagroomtaart', '4x petit four'],
    heat: 'geen',
    tempLossPerSec: 0,
    sensitivity: 2.6,
    basePay: 12,
    maxTip: 7,
    weights: { time: 0.3, temp: 0, care: 0.7 },
    minRank: 1,
    weight: 3,
    deadlineFactor: 1.2,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'petitfours',
    get name() { return t('product.petitfours.name' as MessageKey); },
    icon: '🍓',
    items: ['8x petit four framboos', '2x muntthee'],
    heat: 'geen',
    tempLossPerSec: 0,
    sensitivity: 2.2,
    basePay: 11,
    maxTip: 6,
    weights: { time: 0.35, temp: 0, care: 0.65 },
    minRank: 2,
    weight: 2,
    deadlineFactor: 1.15,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'ijstaart',
    get name() { return t('product.ijstaart.name' as MessageKey); },
    icon: '🍨',
    items: ['1x ijstaart vanille-aardbei'],
    heat: 'koud',
    tempLossPerSec: 0.8,
    sensitivity: 1.3,
    basePay: 13,
    maxTip: 7,
    weights: { time: 0.35, temp: 0.45, care: 0.2 },
    minRank: 2,
    weight: 2,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
  {
    id: 'bruidstaart',
    get name() { return t('product.bruidstaart.name' as MessageKey); },
    icon: '💒',
    items: ['1x bruidstaart van drie lagen'],
    heat: 'geen',
    tempLossPerSec: 0,
    sensitivity: 3.4,
    basePay: 32,
    maxTip: 18,
    weights: { time: 0.25, temp: 0, care: 0.75 },
    minRank: 2,
    weight: 0, // wordt los aangeboden, zie orders.ts
    fillsBox: true,
    deadlineFactor: 1.4,
    get tip() { return t(('product.' + this.id + '.tip') as MessageKey); },
  },
];

export const productById = (id: string): Product | undefined => PRODUCTS.find((p) => p.id === id);
